import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from 'lucide-react'

interface NewsArticle {
    id: string
    title: string
    excerpt: string
    content: string
    category: string
    image: string
    tag: string
    date: string
    isPublished: boolean
}

const NEWS_CATEGORIES = ['NEET Updates', 'Exam Alerts', 'Results', 'Counselling', 'Policy', 'Science & Medicine']
const NEWS_TAGS = ['Breaking', 'Important', 'Guide', 'Results', 'Legal', 'New', 'International', 'Government']

const defaultNews: NewsArticle[] = [
    {
        id: 'an1',
        title: 'NEET UG 2025 Official Exam Date Announced by NTA',
        excerpt: 'The National Testing Agency (NTA) has officially announced the NEET UG 2025 exam date. Students are advised to check the official website.',
        content: '',
        category: 'NEET Updates',
        image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b6f5d?w=600&h=350&fit=crop',
        tag: 'Breaking',
        date: '2025-03-15',
        isPublished: true,
    },
    {
        id: 'an2',
        title: 'New Medical Colleges Approved by NMC for 2025 Academic Year',
        excerpt: 'The National Medical Commission has approved 25 new medical colleges across 12 states, adding approximately 3,200 MBBS seats.',
        content: '',
        category: 'Policy',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=350&fit=crop',
        tag: 'New',
        date: '2025-02-28',
        isPublished: true,
    },
]

const emptyForm = {
    title: '', excerpt: '', content: '', category: NEWS_CATEGORIES[0],
    image: '', tag: NEWS_TAGS[0], date: new Date().toISOString().split('T')[0], isPublished: true,
}

export function AdminNewsView() {
    const [articles, setArticles] = useState<NewsArticle[]>(defaultNews)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({ ...emptyForm })
    const [filterCategory, setFilterCategory] = useState('All')

    const openCreate = () => {
        setEditingId(null)
        setForm({ ...emptyForm })
        setModalOpen(true)
    }

    const openEdit = (a: NewsArticle) => {
        setEditingId(a.id)
        setForm({
            title: a.title, excerpt: a.excerpt, content: a.content,
            category: a.category, image: a.image, tag: a.tag, date: a.date, isPublished: a.isPublished,
        })
        setModalOpen(true)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (editingId) {
            setArticles(prev => prev.map(a => a.id === editingId ? { ...a, ...form } : a))
        } else {
            setArticles(prev => [...prev, { ...form, id: `an-${Date.now()}` }])
        }
        setModalOpen(false)
    }

    const handleDelete = (id: string) => {
        if (confirm('Delete this news article?')) setArticles(prev => prev.filter(a => a.id !== id))
    }

    const togglePublish = (id: string) => {
        setArticles(prev => prev.map(a => a.id === id ? { ...a, isPublished: !a.isPublished } : a))
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const url = URL.createObjectURL(e.target.files[0])
            setForm(f => ({ ...f, image: url }))
        }
    }

    const filtered = filterCategory === 'All' ? articles : articles.filter(a => a.category === filterCategory)

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">News Management</h2>
                    <p className="text-sm text-theme-secondary mt-1">Manage news articles shown on the public News page</p>
                </div>
                <button
                    onClick={openCreate}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors"
                >
                    <Plus className="h-5 w-5" /> Add News Article
                </button>
            </div>

            {/* Filter bar */}
            <div className="flex gap-2 flex-wrap">
                {['All', ...NEWS_CATEGORIES].map(cat => (
                    <button
                        key={cat}
                        onClick={() => setFilterCategory(cat)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filterCategory === cat ? 'bg-primary-blue text-white' : 'text-theme-secondary bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-primary-blue">{articles.length}</p>
                    <p className="text-xs text-theme-secondary mt-1">Total Articles</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{articles.filter(a => a.isPublished).length}</p>
                    <p className="text-xs text-theme-secondary mt-1">Published</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-slate-500">{articles.filter(a => !a.isPublished).length}</p>
                    <p className="text-xs text-theme-secondary mt-1">Drafts</p>
                </GlassmorphicCard>
            </div>

            {/* Articles grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map(a => (
                    <GlassmorphicCard key={a.id} className="overflow-hidden">
                        <div className="relative h-36 overflow-hidden">
                            {a.image ? (
                                <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center">
                                    <span className="text-theme-muted text-sm">No Image</span>
                                </div>
                            )}
                            <div className="absolute top-2 left-2 flex gap-1.5">
                                <span className="px-2 py-0.5 bg-primary-blue text-white text-xs font-semibold rounded-full">{a.category}</span>
                                <span className="px-2 py-0.5 bg-white/90 dark:bg-slate-800/90 text-theme-primary text-xs rounded-full">{a.tag}</span>
                            </div>
                            <div className="absolute top-2 right-2 flex gap-1">
                                <button onClick={() => togglePublish(a.id)} className={`p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 ${a.isPublished ? 'text-green-600' : 'text-slate-400'}`}>
                                    {a.isPublished ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                                <button onClick={() => openEdit(a)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-theme-primary">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="font-semibold text-theme-primary text-sm leading-snug line-clamp-2 mb-1">{a.title}</h3>
                            <p className="text-xs text-theme-secondary line-clamp-2 mb-2">{a.excerpt}</p>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-theme-muted">{a.date}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.isPublished ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-theme-secondary'}`}>
                                    {a.isPublished ? 'Published' : 'Draft'}
                                </span>
                            </div>
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit Article' : 'Add News Article'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Headline / Title *</label>
                                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    placeholder="News headline..." required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Excerpt / Summary *</label>
                                <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    rows={3} placeholder="Short description shown in listings..." required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Full Content</label>
                                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    rows={5} placeholder="Full article content (supports plain text)..." />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Category *</label>
                                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" required>
                                        {NEWS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Tag / Badge</label>
                                    <select value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm">
                                        {NEWS_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Publish Date</label>
                                    <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Cover Image</label>
                                    <input type="file" accept="image/*" onChange={handleImageUpload}
                                        className="w-full text-sm text-theme-secondary file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary-blue/10 file:text-primary-blue" />
                                </div>
                            </div>
                            {form.image && (
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Image URL (or paste URL)</label>
                                    <input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                        placeholder="https://..." />
                                    <img src={form.image} alt="" className="mt-2 h-20 rounded-lg object-cover" />
                                </div>
                            )}
                            {!form.image && (
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Image URL</label>
                                    <input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                        placeholder="https://..." />
                                </div>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.isPublished} onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Publish on public News page</span>
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary text-sm">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold text-sm">Save Article</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
